import createOpenAPIClient, {
  FetchOptions,
  ParamsOption as ParamsInternal,
  RequestBodyOption as RequestBodyInternal,
} from 'openapi-fetch';
import type {
  FilterKeys,
  PathsWithMethod,
  HttpMethod,
  SuccessResponseJSON,
} from 'openapi-typescript-helpers';
import qs from 'qs';
import * as Sentry from '@sentry/react';

import { getToken } from '../token';
import config from '../../config/config';

import { paths as AccountingTypes } from './types/Accounting';
import { paths as AuthenticationTypes } from './types/Authentication';
import { paths as ContentTypes } from './types/Content';
import { paths as UsersTypes } from './types/Users';
import { paths as PartnersTypes } from './types/Partners';
import { paths as ToolsTypes } from './types/Tools';
import { FetchError } from '../../types/entities';
import { chunk, uniq } from '../utils/nativeLodash';

export type { AccountingTypes, AuthenticationTypes, ContentTypes, UsersTypes, PartnersTypes };

export type Params<
  Paths extends object,
  P extends keyof Paths,
  M extends HttpMethod,
> = ParamsInternal<M extends keyof Paths[P] ? Paths[P][M] : unknown>['params'];

type QueryInternal<T> = Extract<
  ParamsInternal<T>['params'] extends { query?: object }
    ? ParamsInternal<T>['params']['query']
    : never,
  object
>;

export type RequestBody<
  Paths extends object,
  P extends keyof Paths,
  M extends HttpMethod,
> = RequestBodyInternal<M extends keyof Paths[P] ? Paths[P][M] : unknown>['body'];

type ResponseDataInternal<
  Paths extends Record<string, Record<HttpMethod, object>>,
  P extends keyof Paths,
  M extends HttpMethod,
> = SuccessResponseJSON<Paths[P][M]>;

export type ResponseData<
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  Paths extends {},
  P extends keyof Paths,
  M extends HttpMethod,
> = ResponseDataInternal<Paths, P, M>;

// We are missing types for the error response body
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createError(body: any, response: Response) {
  let errorType = 'Server error';
  if (response.status >= 400 && response.status < 500) {
    errorType = 'Client error';
  }

  const message = body ? JSON.stringify(body) : response.statusText;

  const error = new FetchError(
    `${errorType}: ${response.status} ${message}`,
    errorType,
    response.status,
    body,
  );

  Sentry.captureException(error);

  return error;
}

// eslint-disable-next-line  @typescript-eslint/no-empty-object-type
function createClient<Paths extends {}>(clientOptions: Parameters<typeof createOpenAPIClient>[0]) {
  const options = {
    querySerializer: (query: unknown) => {
      return qs.stringify(query, { arrayFormat: 'repeat' });
    },
  };

  const client = createOpenAPIClient<Paths>({ ...options, ...clientOptions });

  function addAuthorizationHeader<P extends PathsWithMethod<Paths, M>, M extends HttpMethod>(
    options: FetchOptions<FilterKeys<Paths[P], M>>,
  ) {
    // @ts-expect-error test
    if (clientOptions?.headers && clientOptions?.headers.Authorization) {
      return { ...options };
    }

    return {
      ...options,
      headers: { Authorization: `Bearer ${getToken()}`, ...options.headers },
    };
  }

  return {
    async GET<P extends PathsWithMethod<Paths, 'get'>>(
      url: P,
      init: FetchOptions<FilterKeys<Paths[P], 'get'>>,
    ) {
      const { data, error, response } = await client.GET(url, addAuthorizationHeader(init));

      if (data) {
        return { data, response };
      }

      throw createError(error, response);
    },

    /**
     * Makes multiple GET requests to the endpoint, splitting the query parameter
     * for the provided key into chunks to make sure the URL doesn't get too long
     * resulting in an error.
     * This will return an object with pages which is an array of all the responses.
     */
    async GET_CHUNKED<P extends PathsWithMethod<Paths, 'get'>>(
      url: P,
      init: FetchOptions<FilterKeys<Paths[P], 'get'>> & {
        skipCache?: boolean;
        chunkKey: keyof QueryInternal<FilterKeys<Paths[P], 'get'>>;
      },
    ) {
      /* The types becomes very messy here since they are all generics with a lot of subtypes,
       * to make it easier to work with we use `any` but
       * all the types are correctly inferred and checked when calling the function
       */
      const CHUNK_SIZE = 15;
      const queryObject =
        init.params && 'query' in init.params && init.params.query
          ? init.params.query
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ({} as any);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ids: any[] = queryObject ? queryObject[init.chunkKey as string] : [];
      const chunks = chunk(uniq(ids).sort(), CHUNK_SIZE);

      const pages = [];
      for (const chunk of chunks) {
        const { data } = await this.GET<P>(url, {
          ...init,
          params: {
            ...init.params,
            query: { ...queryObject, limit: CHUNK_SIZE, [init.chunkKey]: chunk },
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);

        pages.push(data);
      }

      /* To make sure the types are correct we need to return
       * each response in the array. We can't for example use flatMap here
       * to combine the results into one array since we don't really know the type
       * and the resulting array will become any[].
       */
      return { pages };
    },

    async PUT<P extends PathsWithMethod<Paths, 'put'>>(
      url: P,
      init: FetchOptions<FilterKeys<Paths[P], 'put'>>,
    ) {
      const { data, error, response } = await client.PUT(url, addAuthorizationHeader(init));

      if (data) {
        return { data, response };
      }

      throw createError(error, response);
    },

    async POST<P extends PathsWithMethod<Paths, 'post'>>(
      url: P,
      init: FetchOptions<FilterKeys<Paths[P], 'post'>>,
    ) {
      const { data, error, response } = await client.POST(url, addAuthorizationHeader(init));

      if (data) {
        return { data, response };
      }

      throw createError(error, response);
    },

    async DELETE<P extends PathsWithMethod<Paths, 'delete'>>(
      url: P,
      init: FetchOptions<FilterKeys<Paths[P], 'delete'>>,
    ) {
      const { data, error, response } = await client.DELETE(url, addAuthorizationHeader(init));

      if (data) {
        return { data, response };
      }

      throw createError(error, response);
    },
  };
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: string | FormData;
};

async function rawFetch<Paths extends object, P extends keyof Paths, M extends HttpMethod>(
  url: string,
  options?: RequestOptions,
): Promise<{
  data: ResponseData<Paths, P, M>;
  response: Response;
}>;

async function rawFetch<T>(
  url: string,
  options: RequestOptions = {},
): Promise<{ data: T | undefined; response: Response }> {
  const headers: RequestOptions['headers'] = {
    Authorization: `Bearer ${getToken()}`,
    'x-application-name': 'alliance',
    ...options.headers,
  };

  if (options.body && !headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method: options.method || 'GET',
    headers,
    body: options.body,
    credentials: 'include',
  });

  if (!response.ok) {
    await handleErrors(response);
  }

  if (response.status === 204) {
    return { response, data: undefined };
  }

  const data = await parseData(response);

  return { data, response };

  async function parseData(response: Response) {
    if (response.headers.get('content-type')?.includes('application/json')) {
      return await response.clone().json();
    }

    return await response.clone().text();
  }

  async function handleErrors(response: Response) {
    let json;
    try {
      json = await response.json();
      // eslint-disable-next-line no-empty
    } catch (_) {}

    throw createError(json, response);
  }
}

export function createGalaxyClient({
  token,
  baseUrl = process.env.API_URL || config.apiUrl,
}: { token?: string; baseUrl?: string } = {}) {
  const headers = token
    ? { Authorization: `Bearer ${token}`, 'x-application-name': 'alliance' }
    : { 'x-application-name': 'alliance' };
  return {
    authentication: createClient<AuthenticationTypes>({
      baseUrl: `${baseUrl}/authentication`,
      headers,
    }),
    accounting: createClient<AccountingTypes>({
      baseUrl: `${baseUrl}/accounting`,
      headers,
    }),
    content: createClient<ContentTypes>({
      baseUrl: `${baseUrl}/content`,
      headers,
    }),
    partners: createClient<PartnersTypes>({
      baseUrl: `${baseUrl}/partners`,
      headers,
    }),
    users: createClient<UsersTypes>({
      baseUrl: `${baseUrl}/users`,
      headers,
    }),
    tools: createClient<ToolsTypes>({
      baseUrl: `${baseUrl}/tools`,
      headers,
    }),
    fetch: rawFetch,
  };
}

export const client = createGalaxyClient();
